import { useState } from 'react';
import type { FormEvent } from 'react';
import { createFirstMember, openSession } from './api';
import { navigate, paths } from './router';
import { Banner, Button, LogoLockup, TextInput } from './ui';

export interface LoginProps {
  /** Session ouverte : l'application recharge l'écran demandé. */
  onAuthenticated: () => void;
  /**
   * Aucun membre n'existe encore (US-42). L'écran ne demande alors pas de se
   * connecter mais de **créer le premier compte** : il n'y a personne à qui
   * demander un mot de passe, et afficher un formulaire de connexion que rien
   * ne peut satisfaire serait une impasse.
   */
  bootstrap?: boolean;
  /**
   * L'instance sait-elle envoyer un message ? (US-53) Sans SMTP, les routes de
   * réinitialisation répondent 404 : proposer le lien mènerait à une impasse,
   * ce qui est pire que de ne rien proposer.
   */
  passwordReset?: boolean;
}

/**
 * Connexion (US-42 ; auparavant mot de passe de foyer, US-19).
 *
 * **On se connecte avec son adresse depuis US-52.** Le nom ne pouvait pas
 * rester l'identifiant : il doit être unique pour cela, or deux personnes d'une
 * même famille peuvent porter le même prénom — et surtout, on ne réinitialise
 * pas un mot de passe sans savoir où écrire. L'adresse est mémorisée par
 * appareil : sur la tablette de la cuisine, c'est toujours la même personne qui
 * la saisit, et la retaper à chaque fois n'apporte aucune sécurité.
 *
 * **Le mot de passe n'est jamais mémorisé côté client.** Le cookie de session
 * signé (`HttpOnly`) est la seule chose qui persiste, et il est hors de portée
 * du JavaScript de la page.
 */
export function Login({ onAuthenticated, bootstrap = false, passwordReset = false }: LoginProps): React.JSX.Element {
  const [email, setEmail] = useState(() => {
    try {
      return localStorage.getItem('mlc:dernier-membre') ?? '';
    } catch {
      return '';
    }
  });
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);

    const action = bootstrap
      ? createFirstMember({ first_name: firstName, last_name: lastName, email, password })
      : openSession(email, password);
    action
      .then(() => {
        try {
          localStorage.setItem('mlc:dernier-membre', email.trim().toLowerCase());
        } catch { /* stockage indisponible : l'adresse sera à ressaisir, sans plus */ }
        setPassword('');
        onAuthenticated();
      })
      .catch((e: unknown) => { setError(e instanceof Error ? e.message : String(e)); })
      .finally(() => { setSubmitting(false); });
  }

  return (
    // `max-w-sm` (384 px) retiré : le conteneur de l'écran porte désormais la
    // largeur, et deux plafonds superposés donnaient une carte de 290 px.
    <form onSubmit={handleSubmit} className="flex w-full flex-col gap-5">
      {/* Seul écran vu avant toute recette : c'est là que l'application se
          présente, signature complète et baseline comprise. Le `h1` porte la
          signature plutôt qu'un titre en double au-dessus d'elle. */}
      <h1 className="mb-2 flex justify-center">
        <LogoLockup />
      </h1>
      <p className="text-center text-lede text-balance">
        {bootstrap
          ? 'Personne n’a encore de compte sur cette instance. Créez le vôtre : vous en serez le propriétaire.'
          : 'Entrez votre adresse e-mail et votre mot de passe pour accéder au livre.'}
      </p>

      {error !== null && <Banner tone="danger">{error}</Banner>}

      {/* Le prénom n'est demandé qu'à la création : c'est le nom que l'ensemble
          de l'application affichera — « de Claire », « modifiée par Claire ». */}
      {bootstrap && (
        <>
          <label className="flex flex-col gap-1 text-sm">
            Prénom
            <TextInput
              type="text"
              autoComplete="given-name"
              value={firstName}
              onChange={(e) => { setFirstName(e.target.value); }}
            />
          </label>
          <label className="flex flex-col gap-1 text-sm">
            {/* Un seul élément de flux : voir MemberSettings.tsx — dans un
              libellé en colonne, un nœud de texte suivi d'un `<span>` occupe
              deux lignes et désaligne le champ voisin. */}
          <span>Nom <span className="text-foreground-muted">(facultatif)</span></span>
            <TextInput
              type="text"
              autoComplete="family-name"
              value={lastName}
              onChange={(e) => { setLastName(e.target.value); }}
            />
          </label>
        </>
      )}

      <label className="flex flex-col gap-1 text-sm">
        Adresse e-mail
        <TextInput
          type="email"
          inputMode="email"
          autoComplete="username"
          value={email}
          onChange={(e) => { setEmail(e.target.value); }}
        />
      </label>

      <label className="flex flex-col gap-1 text-sm">
        Mot de passe
        <TextInput
          type="password"
          autoComplete={bootstrap ? 'new-password' : 'current-password'}
          value={password}
          onChange={(e) => { setPassword(e.target.value); }}
        />
      </label>

      {bootstrap && (
        <p className="text-xs text-foreground-secondary">
          Huit caractères au minimum. Ce mot de passe n’est stocké nulle part en clair — s’il est perdu, il
          faudra en poser un nouveau depuis le serveur.
        </p>
      )}

      <Button type="submit" variant="primary" disabled={submitting} aria-busy={submitting}>
        {bootstrap ? 'Créer mon compte' : 'Se connecter'}
      </Button>

      {!bootstrap && passwordReset && (
        <Button type="button" variant="ghost" onClick={() => { navigate(paths.resetPassword); }}>
          Mot de passe oublié ?
        </Button>
      )}
    </form>
  );
}
